import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Play,
  CheckCircle,
  Circle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Award,
  FileText,
  HelpCircle,
  PlayCircle,
  Menu,
  X,
  MessageSquare,
  Download,
  Share2,
  ThumbsUp,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TNC_CLAUSES = [
  {
    title: "1. Paid Content Acknowledgment",
    body: "This program is a paid course that contains proprietary knowledge, strategies, and materials of high value. By accessing this course, you acknowledge the investment made to create and deliver this content."
  },
  {
    title: "2. Personal Use Only",
    body: "All materials provided in this course are strictly for your personal learning and development. They are not to be shared, reproduced, or used for commercial purposes without prior written permission."
  },
  {
    title: "3. No Unauthorized Distribution",
    body: "You agree not to copy, distribute, resell, upload, or share any part of this course (including videos, PDFs, recordings, or resources) with others, whether publicly or privately."
  },
  {
    title: "4. Intellectual Property Protection",
    body: "All content remains the intellectual property of the course creator. Unauthorized use or duplication is a violation of these terms and may result in legal action."
  },
  {
    title: "5. Respect for Community & Integrity",
    body: "This space is built on trust and integrity. By participating, you agree to honor the value of the program and respect the effort behind it."
  },
  {
    title: "6. Access Control",
    body: "Your access to the course is granted solely to you. Sharing login details or allowing others to access the course through your account is strictly prohibited."
  },
  {
    title: "7. Enforcement",
    body: "Any breach of these terms may result in immediate termination of access without refund."
  }
];

const LearnPage = ({ user }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('content');
  
  // T&C state
  const [tncAgreed, setTncAgreed] = useState(false);
  const [tncLoading, setTncLoading] = useState(true);
  const [tncChecked, setTncChecked] = useState(false);
  const [tncSubmitting, setTncSubmitting] = useState(false);

  // Fetch course and enrollment data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch course
        const courseResponse = await fetch(`${BACKEND_URL}/api/courses/${slug}`);
        if (!courseResponse.ok) {
          navigate('/courses');
          return;
        }
        const courseData = await courseResponse.json();
        setCourse(courseData);

        // Fetch enrollment
        if (user) {
          const enrollmentResponse = await fetch(`${BACKEND_URL}/api/enrollments/me`, {
            credentials: 'include'
          });
          if (enrollmentResponse.ok) {
            const enrollments = await enrollmentResponse.json();
            const courseEnrollment = enrollments.find(e => e.course.id === courseData.id);
            if (courseEnrollment) {
              setEnrollment(courseEnrollment.enrollment);
              setCompletedLessons(courseEnrollment.enrollment.completed_lessons || []);
            }
          }
          
          // Check T&C status
          const tncResponse = await fetch(`${BACKEND_URL}/api/courses/${courseData.id}/tnc-status`, {
            credentials: 'include'
          });
          if (tncResponse.ok) {
            const tncData = await tncResponse.json();
            setTncAgreed(tncData.agreed);
          }
        }
        setTncLoading(false);

        // Set initial lesson
        if (courseData.sections && courseData.sections.length > 0) {
          const firstSection = courseData.sections[0];
          if (firstSection.lessons && firstSection.lessons.length > 0) {
            setCurrentLesson(firstSection.lessons[0]);
            setCurrentSectionIndex(0);
            setCurrentLessonIndex(0);
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, user, navigate]);
  
  // Handle T&C agreement
  const handleAgreeToTnc = async () => {
    if (!course || !tncChecked) return;
    setTncSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/${course.id}/agree-tnc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        setTncAgreed(true);
      }
    } catch (error) {
      console.error('Error agreeing to T&C:', error);
    } finally {
      setTncSubmitting(false);
    }
  };

  // Calculate progress
  const totalLessons = course?.sections?.reduce(
    (acc, section) => acc + (section.lessons?.length || 0), 0
  ) || 0;
  const progressPercent = totalLessons > 0 
    ? (completedLessons.length / totalLessons) * 100 
    : 0;

  // Get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  // Mark lesson as complete
  const markLessonComplete = useCallback(async (lessonId) => {
    if (!enrollment || completedLessons.includes(lessonId)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/enrollments/${enrollment.id}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lesson_id: lessonId, completed: true })
      });

      if (response.ok) {
        setCompletedLessons(prev => [...prev, lessonId]);
      }
    } catch (error) {
      console.error('Error marking lesson complete:', error);
    }
  }, [enrollment, completedLessons]);

  // Navigate to lesson
  const goToLesson = (sectionIndex, lessonIndex) => {
    const section = course.sections[sectionIndex];
    if (section && section.lessons[lessonIndex]) {
      setCurrentLesson(section.lessons[lessonIndex]);
      setCurrentSectionIndex(sectionIndex);
      setCurrentLessonIndex(lessonIndex);
    }
  };

  // Navigate to next lesson
  const goToNextLesson = () => {
    if (!course?.sections) return;
    
    const currentSection = course.sections[currentSectionIndex];
    if (currentLessonIndex < currentSection.lessons.length - 1) {
      goToLesson(currentSectionIndex, currentLessonIndex + 1);
    } else if (currentSectionIndex < course.sections.length - 1) {
      goToLesson(currentSectionIndex + 1, 0);
    }
  };

  // Navigate to previous lesson
  const goToPrevLesson = () => {
    if (currentLessonIndex > 0) {
      goToLesson(currentSectionIndex, currentLessonIndex - 1);
    } else if (currentSectionIndex > 0) {
      const prevSection = course.sections[currentSectionIndex - 1];
      goToLesson(currentSectionIndex - 1, prevSection.lessons.length - 1);
    }
  };

  // Check if there's a next/prev lesson
  const hasNextLesson = () => {
    if (!course?.sections) return false;
    const currentSection = course.sections[currentSectionIndex];
    return currentLessonIndex < currentSection.lessons.length - 1 || 
           currentSectionIndex < course.sections.length - 1;
  };

  const hasPrevLesson = () => {
    return currentLessonIndex > 0 || currentSectionIndex > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Course not found</p>
          <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
        </div>
      </div>
    );
  }

  // T&C Gate - show overlay if not yet agreed
  if (!tncLoading && !tncAgreed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="tnc-gate">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <ShieldCheck className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Terms & Conditions</h1>
            <p className="text-gray-400 text-sm">
              Please read and agree to the following terms before accessing <span className="text-amber-400 font-medium">{course.title}</span>
            </p>
          </div>

          {/* Clauses Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="max-h-[55vh] overflow-y-auto p-6 space-y-5" data-testid="tnc-clauses-scroll">
              {TNC_CLAUSES.map((clause, idx) => (
                <div key={idx} className="group">
                  <h3 className="text-amber-400 font-semibold text-sm mb-1.5">{clause.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{clause.body}</p>
                  {idx < TNC_CLAUSES.length - 1 && <div className="mt-5 border-b border-gray-800/60" />}
                </div>
              ))}
            </div>

            {/* Agreement Footer */}
            <div className="border-t border-gray-800 bg-gray-900/80 p-5 space-y-4">
              <div className="flex items-center gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-xs text-amber-300/80">
                  This is a paid course (value more than $1000). Property of Steven Yong. Cannot be distributed without permission.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="tnc-checkbox-label">
                <input
                  type="checkbox"
                  checked={tncChecked}
                  onChange={(e) => setTncChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-800 text-amber-500 focus:ring-amber-500 focus:ring-offset-gray-900 cursor-pointer"
                  data-testid="tnc-checkbox"
                />
                <span className="text-sm text-gray-300">
                  I have read, understood, and agree to all the Terms & Conditions stated above.
                </span>
              </label>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                  data-testid="tnc-decline-btn"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleAgreeToTnc}
                  disabled={!tncChecked || tncSubmitting}
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="tnc-agree-btn"
                >
                  {tncSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      I Agree & Continue
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Top Navigation Bar */}
      <div className="h-14 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="h-6 w-px bg-gray-700" />
          <h1 className="text-white font-medium truncate max-w-md">{course.title}</h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2">
            <span className="text-sm text-gray-400">
              {completedLessons.length}/{totalLessons} lessons
            </span>
            <Progress value={progressPercent} className="w-32 h-2" />
            <span className="text-sm text-emerald-400">{Math.round(progressPercent)}%</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white md:hidden"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 pt-14">
        {/* Video/Content Area */}
        <div className={`flex-1 flex flex-col ${sidebarOpen ? 'md:mr-80' : ''}`}>
          {/* Video Player */}
          <div className="bg-black">
            <div className="aspect-video max-h-[70vh] mx-auto">
              {currentLesson?.type === 'video' && currentLesson?.video_url ? (
                <iframe
                  src={getYouTubeEmbedUrl(currentLesson.video_url)}
                  title={currentLesson.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : currentLesson?.type === 'quiz' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <HelpCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Quiz: {currentLesson.title}</h2>
                    <p className="text-gray-400 mb-6">{currentLesson.questions || 10} questions</p>
                    <Button className="bg-emerald-600 hover:bg-emerald-500">
                      Start Quiz
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">{currentLesson?.title}</h2>
                    <p className="text-gray-400">Content will be displayed here</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lesson Info & Navigation */}
          <div className="p-6 border-b border-gray-800">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-gray-800 text-gray-300">
                      Section {currentSectionIndex + 1}, Lesson {currentLessonIndex + 1}
                    </Badge>
                    {currentLesson?.duration && (
                      <Badge variant="secondary" className="bg-gray-800 text-gray-300">
                        <Clock className="w-3 h-3 mr-1" />
                        {currentLesson.duration}
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-white">{currentLesson?.title}</h2>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPrevLesson}
                    disabled={!hasPrevLesson()}
                    className="border-gray-700 text-white hover:bg-gray-800"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  
                  {completedLessons.includes(currentLesson?.id) ? (
                    <Button
                      size="sm"
                      onClick={goToNextLesson}
                      disabled={!hasNextLesson()}
                      className="bg-emerald-600 hover:bg-emerald-500"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => {
                        markLessonComplete(currentLesson?.id);
                        if (hasNextLesson()) {
                          setTimeout(goToNextLesson, 300);
                        }
                      }}
                      className="bg-emerald-600 hover:bg-emerald-500"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Mark Complete
                    </Button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-800">
                <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                  <ThumbsUp className="w-5 h-5" />
                  <span className="text-sm">Like</span>
                </button>
                <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                  <MessageSquare className="w-5 h-5" />
                  <span className="text-sm">Discussion</span>
                </button>
                <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                  <Download className="w-5 h-5" />
                  <span className="text-sm">Resources</span>
                </button>
                <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                  <Share2 className="w-5 h-5" />
                  <span className="text-sm">Share</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <div className="flex-1 p-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6 border-b border-gray-800">
                <button
                  onClick={() => setActiveTab('content')}
                  className={`pb-3 px-1 text-sm font-medium transition-colors ${
                    activeTab === 'content'
                      ? 'text-emerald-400 border-b-2 border-emerald-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`pb-3 px-1 text-sm font-medium transition-colors ${
                    activeTab === 'notes'
                      ? 'text-emerald-400 border-b-2 border-emerald-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Notes
                </button>
                <button
                  onClick={() => setActiveTab('resources')}
                  className={`pb-3 px-1 text-sm font-medium transition-colors ${
                    activeTab === 'resources'
                      ? 'text-emerald-400 border-b-2 border-emerald-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Resources
                </button>
              </div>

              {activeTab === 'content' && (
                <div className="prose prose-invert max-w-none">
                  <h3 className="text-lg font-semibold text-white mb-4">About this lesson</h3>
                  <p className="text-gray-400">
                    {currentLesson?.content || 
                      `In this lesson, you will learn about "${currentLesson?.title}". 
                      Watch the video above and follow along with the instructor.`}
                  </p>
                  
                  {course.what_you_will_learn && (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold text-white mb-3">Course objectives</h4>
                      <ul className="space-y-2">
                        {course.what_you_will_learn.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-400">
                            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'notes' && (
                <div>
                  <textarea
                    placeholder="Take notes for this lesson..."
                    className="w-full h-64 p-4 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 resize-none"
                  />
                  <Button className="mt-4 bg-emerald-600 hover:bg-emerald-500">
                    Save Notes
                  </Button>
                </div>
              )}

              {activeTab === 'resources' && (
                <div className="text-center py-8">
                  <Download className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No downloadable resources for this lesson</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Course Content */}
        <div
          className={`fixed right-0 top-14 bottom-0 w-80 bg-gray-900 border-l border-gray-800 flex flex-col transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Course Content</h3>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-gray-400 hover:text-white md:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={progressPercent} className="flex-1 h-2" />
              <span className="text-sm text-gray-400">{Math.round(progressPercent)}%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {completedLessons.length} of {totalLessons} lessons completed
            </p>
          </div>

          <ScrollArea className="flex-1">
            <Accordion 
              type="multiple" 
              defaultValue={course.sections?.map(s => s.id) || []}
              className="px-2 py-2"
            >
              {course.sections?.map((section, sectionIndex) => (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  className="border-b border-gray-800/50"
                >
                  <AccordionTrigger className="px-3 py-3 hover:no-underline hover:bg-gray-800/50 rounded-lg">
                    <div className="flex items-center gap-3 text-left">
                      <div className="text-xs text-gray-500 w-6">
                        {sectionIndex + 1}
                      </div>
                      <div className="flex-1">
                        <span className="text-white text-sm font-medium block">
                          {section.title}
                        </span>
                        <span className="text-xs text-gray-500">
                          {section.lessons?.filter(l => completedLessons.includes(l.id)).length || 0}/
                          {section.lessons?.length || 0} completed
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pb-2">
                    <div className="space-y-1">
                      {section.lessons?.map((lesson, lessonIndex) => {
                        const isCompleted = completedLessons.includes(lesson.id);
                        const isCurrent = currentLesson?.id === lesson.id;
                        
                        return (
                          <button
                            key={lesson.id}
                            onClick={() => goToLesson(sectionIndex, lessonIndex)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                              isCurrent
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'hover:bg-gray-800/50 text-gray-300'
                            }`}
                          >
                            <div className="w-6 flex justify-center">
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                              ) : isCurrent ? (
                                <PlayCircle className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <Circle className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm block truncate ${
                                isCurrent ? 'text-emerald-400' : isCompleted ? 'text-gray-400' : 'text-gray-300'
                              }`}>
                                {lesson.title}
                              </span>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                {lesson.type === 'video' && <Play className="w-3 h-3" />}
                                {lesson.type === 'quiz' && <HelpCircle className="w-3 h-3" />}
                                {lesson.type === 'text' && <FileText className="w-3 h-3" />}
                                {lesson.duration && <span>{lesson.duration}</span>}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollArea>

          {/* Completion Card */}
          {progressPercent >= 100 && (
            <div className="p-4 border-t border-gray-800">
              <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
                <CardContent className="p-4 text-center">
                  <Award className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <h4 className="text-white font-semibold mb-1">Course Completed!</h4>
                  <p className="text-gray-400 text-sm mb-3">Congratulations on finishing the course</p>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500">
                    Get Certificate
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
